/**
 * Visitor attribution for the public approvals demo (SKE-4166, phase 3).
 *
 * The demo is an ad landing page, so the single most valuable thing to capture
 * alongside the visitor's email is *where they came from*. This module owns
 * that capture and nothing else.
 *
 * FIRST-TOUCH, STICKY FOR THE VISIT
 * ---------------------------------
 * The SPA rewrites `location.hash` the moment it mounts (`ensureDemoRoute`),
 * and every later navigation churns it again, so reading UTMs at submit time
 * would frequently read *nothing*. Attribution is therefore captured on the
 * first call of the page load and pinned in `sessionStorage`, which is:
 *
 *   - per-tab and per-visit, matching how an ad click maps to a session;
 *   - separate from the demo store (`localStorage`), so "Reset demo" — which
 *     resets the *board* — never discards the campaign the visitor arrived on.
 *
 * WHERE THE PARAMS LIVE
 * ---------------------
 * `try.skedsocial.com/approvals?utm_source=…#/review/demo` puts them in
 * `location.search`, which is the normal case. A shared/mangled link can also
 * land them after the hash (`#/review/demo?utm_source=…`), so both are parsed —
 * the same tolerance `isDemoMode()` already applies to `?demo=1`.
 */

const ATTRIBUTION_STORAGE_KEY = 'sked.demo.approvals.attribution.v1'
const SESSION_ID_STORAGE_KEY = 'sked.demo.approvals.session.v1'

/**
 * Query params worth carrying. UTMs feed HubSpot + GA4; the click IDs are what
 * the ad platforms themselves match on (see the server's `serverGtm` params).
 */
export interface DemoAttribution {
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmContent?: string
  utmTerm?: string
  gclid?: string
  gbraid?: string
  wbraid?: string
  gadSource?: string
  fbclid?: string
  msclkid?: string
  ttclid?: string
  twclid?: string
  liFatId?: string
  /**
   * Phase-4 hook. `?vertical=agency` on the ad URL is how a campaign declares
   * which flavour of the demo it wants; phase 3 only records it.
   */
  vertical?: string
  /** The URL the visitor landed on, minus the SPA's own hash route. */
  landingPage?: string
  referrer?: string
}

/** Query param -> `DemoAttribution` key. */
const PARAM_MAP: Record<string, keyof DemoAttribution> = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_content: 'utmContent',
  utm_term: 'utmTerm',
  gclid: 'gclid',
  gbraid: 'gbraid',
  wbraid: 'wbraid',
  gad_source: 'gadSource',
  fbclid: 'fbclid',
  msclkid: 'msclkid',
  ttclid: 'ttclid',
  twclid: 'twclid',
  li_fat_id: 'liFatId',
  vertical: 'vertical',
}

/** Anything longer than this is a tracking-parameter bug, not a campaign name. */
const MAX_VALUE_LENGTH = 300

const readParams = (search: string, into: DemoAttribution): void => {
  if (!search) {
    return
  }

  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search
  )

  for (const [param, key] of Object.entries(PARAM_MAP)) {
    // First writer wins: `location.search` is read before the hash, so a real
    // query param is never clobbered by a stale copy carried in the hash.
    if (into[key]) {
      continue
    }
    const value = params.get(param)?.trim()
    if (value) {
      into[key] = value.slice(0, MAX_VALUE_LENGTH)
    }
  }
}

/**
 * Pure parse — exported for tests and reused by `getDemoAttribution`.
 *
 * @param href the full landing URL
 * @param referrer `document.referrer`, when there is one
 */
export const parseDemoAttribution = (
  href: string,
  referrer?: string
): DemoAttribution => {
  const attribution: DemoAttribution = {}

  let url: URL | null = null
  try {
    url = new URL(href)
  } catch {
    // Not parseable (test harness, odd embed) — there is nothing to capture,
    // but a referrer may still be worth keeping.
    url = null
  }

  if (url) {
    readParams(url.search, attribution)

    // `#/review/demo?utm_source=…` — take everything after the hash's own `?`.
    const hashQueryStart = url.hash.indexOf('?')
    if (hashQueryStart !== -1) {
      readParams(url.hash.slice(hashQueryStart), attribution)
    }

    // Landing page without the SPA's internal route, so every visitor on a
    // campaign reports the same landing URL.
    attribution.landingPage = `${url.origin}${url.pathname}${url.search}`.slice(
      0,
      MAX_VALUE_LENGTH * 2
    )
  }

  const trimmedReferrer = referrer?.trim()
  if (trimmedReferrer) {
    attribution.referrer = trimmedReferrer.slice(0, MAX_VALUE_LENGTH * 2)
  }

  return attribution
}

const readSession = (key: string): string | null => {
  try {
    return globalThis.window?.sessionStorage.getItem(key) ?? null
  } catch {
    // Safari private mode / storage disabled.
    return null
  }
}

const writeSession = (key: string, value: string): void => {
  try {
    globalThis.window?.sessionStorage.setItem(key, value)
  } catch {
    // Nothing to do — the in-memory cache below still serves this page load.
  }
}

let cachedAttribution: DemoAttribution | null = null

/**
 * The visitor's attribution, captured on first call and pinned for the visit.
 */
export const getDemoAttribution = (): DemoAttribution => {
  if (cachedAttribution) {
    return cachedAttribution
  }

  const stored = readSession(ATTRIBUTION_STORAGE_KEY)
  if (stored) {
    try {
      cachedAttribution = JSON.parse(stored) as DemoAttribution
      return cachedAttribution
    } catch {
      // Corrupt — fall through and re-capture from the current URL.
    }
  }

  const location = globalThis.window?.location
  cachedAttribution = parseDemoAttribution(
    location?.href ?? '',
    globalThis.document?.referrer
  )
  writeSession(ATTRIBUTION_STORAGE_KEY, JSON.stringify(cachedAttribution))

  return cachedAttribution
}

let cachedSessionId: string | null = null

/**
 * A per-visit id that ties the client analytics events to the server-side lead
 * row — so "gate shown" and "email submitted" can be joined without a login.
 */
export const getDemoSessionId = (): string => {
  if (cachedSessionId) {
    return cachedSessionId
  }

  const stored = readSession(SESSION_ID_STORAGE_KEY)
  if (stored) {
    cachedSessionId = stored
    return stored
  }

  cachedSessionId =
    globalThis.crypto?.randomUUID?.() ??
    `demo-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  writeSession(SESSION_ID_STORAGE_KEY, cachedSessionId)

  return cachedSessionId
}

/**
 * Which flavour of the demo the campaign asked for. Phase 3 records it on every
 * analytics event and on the lead; **phase 4** is what actually personalises
 * the board from it.
 */
export const getDemoVertical = (): string | undefined =>
  getDemoAttribution().vertical

/** Test seam — drops the memoised copies so a spec can re-drive the URL. */
export const resetDemoAttributionForTest = (): void => {
  cachedAttribution = null
  cachedSessionId = null
}
